import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { XMarkIcon } from '@heroicons/react/24/outline';
import StatusNoteModal from './StatusNoteModal';
import { APPLICANT_STATUS_OPTIONS, NOTE_REQUIRED_STATUSES, appendStatusNote } from '../utils/applicantStatus';

const PHONE_DIGIT_LIMIT = 10;

const sanitizeTypedPhone = (rawValue) => String(rawValue || '').replace(/\D/g, '');

const normalizePastedPhone = (rawValue, fallbackCountryCode = '+91') => {
  const sanitized = String(rawValue || '').trim().replace(/[^\d+]/g, '');
  if (!sanitized) {
    return { countryCode: fallbackCountryCode, phone: '' };
  }

  let digitsOnly = sanitized.replace(/\D/g, '');

  if (digitsOnly.startsWith('00')) {
    digitsOnly = digitsOnly.slice(2);
  }

  if (digitsOnly.length === 11 && digitsOnly.startsWith('0')) {
    digitsOnly = digitsOnly.slice(1);
  } else if (digitsOnly.length === 12 && digitsOnly.startsWith('91')) {
    digitsOnly = digitsOnly.slice(2);
  }

  return {
    countryCode: fallbackCountryCode,
    phone: digitsOnly
  };
};

const ApplicantForm = ({ applicant, onClose, defaultJobId, onJobChange }) => {
  const [formData, setFormData] = useState({
    name: '',
    countryCode: '+91',
    phone: '',
    countryCode2: '+91',
    phone2: '',
    email: '',
    position: '',
    status: 'new_lead',
    notes: '',
    hireDate: '',
    salary: '',
    startDate: '',
    department: '',
    employeeId: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [jobs, setJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState(defaultJobId || '');
  const [pendingStatus, setPendingStatus] = useState('');

  const phoneHasTooManyDigits = formData.phone.length > PHONE_DIGIT_LIMIT;
  const phone2HasTooManyDigits = formData.phone2.length > PHONE_DIGIT_LIMIT;
  const positionOptions = useMemo(() => {
    const options = [...new Set(
      jobs
        .map((job) => String(job.position || '').trim())
        .filter(Boolean)
    )].sort((a, b) => a.localeCompare(b));

    const currentPosition = String(formData.position || '').trim();
    if (currentPosition && !options.includes(currentPosition)) {
      options.unshift(currentPosition);
    }

    return options;
  }, [jobs, formData.position]);

  useEffect(() => {
    fetchJobs();
    if (applicant) {
      setFormData({
        name: applicant.name || '',
        countryCode: applicant.countryCode || '+91',
        phone: applicant.phone || '',
        countryCode2: applicant.countryCode2 || '+91',
        phone2: applicant.phone2 || '',
        email: applicant.email || '',
        position: applicant.position || '',
        status: applicant.status || 'new_lead',
        notes: applicant.notes || '',
        hireDate: applicant.hireDate ? applicant.hireDate.split('T')[0] : '',
        salary: applicant.salary || '',
        startDate: applicant.startDate ? applicant.startDate.split('T')[0] : '',
        department: applicant.department || '',
        employeeId: applicant.employeeId || ''
      });
    }
  }, [applicant]);

  // When defaultJobId is set and no applicant (create mode), auto-fill position
  useEffect(() => {
    if (!applicant && selectedJobId && jobs.length > 0) {
      const job = jobs.find(j => String(j.id) === String(selectedJobId));
      if (job) {
        setFormData(prev => ({ ...prev, position: job.position, leadSource: job.site || '' }));
      }
    }
  }, [selectedJobId, jobs]);

  const fetchJobs = async () => {
    try {
      const response = await axios.get('/api/jobs?limit=1000&status=open');
      setJobs(response.data.jobs);
    } catch (err) {}
  };

  const handleJobSelect = (e) => {
    const jobId = e.target.value;
    setSelectedJobId(jobId);
    if (onJobChange) onJobChange(jobId);
    if (jobId) {
      const job = jobs.find(j => String(j.id) === jobId);
      if (job) {
        setFormData(prev => ({ ...prev, position: job.position, leadSource: job.site || '' }));
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'phone' || name === 'phone2') {
      setFormData(prev => ({
        ...prev,
        [name]: sanitizeTypedPhone(value)
      }));
      return;
    }

    if (name === 'status' && NOTE_REQUIRED_STATUSES.includes(value) && value !== formData.status) {
      setPendingStatus(value);
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhonePaste = (e) => {
    const { name } = e.target;
    const pastedValue = e.clipboardData.getData('text');
    const countryField = name === 'phone' ? 'countryCode' : 'countryCode2';
    const { phone } = normalizePastedPhone(pastedValue, formData[countryField] || '+91');

    e.preventDefault();
    setFormData((prev) => ({
      ...prev,
      [name]: phone
    }));
  };

  const handleStatusNoteSave = (note) => {
    setFormData((prev) => ({
      ...prev,
      status: pendingStatus,
      notes: appendStatusNote(prev.notes, pendingStatus, note)
    }));
    setPendingStatus('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Require job selection when creating a new applicant
    if (!applicant && !selectedJobId) {
      setError('Please select a job posting');
      setLoading(false);
      return;
    }

    // Require email when creating a new applicant
    if (!applicant && !formData.email.trim()) {
      setError('Email address is required');
      setLoading(false);
      return;
    }

    if (formData.phone.length !== PHONE_DIGIT_LIMIT) {
      setError('Primary phone number must be exactly 10 digits');
      setLoading(false);
      return;
    }

    if (formData.phone2 && formData.phone2.length !== PHONE_DIGIT_LIMIT) {
      setError('Secondary phone number must be exactly 10 digits');
      setLoading(false);
      return;
    }

    try {
      const payload = { ...formData };
      if (payload.salary) payload.salary = parseFloat(payload.salary);
      if (!payload.hireDate) delete payload.hireDate;
      if (!payload.startDate) delete payload.startDate;
      if (!payload.salary) delete payload.salary;
      if (!payload.department) delete payload.department;
      if (!payload.employeeId) delete payload.employeeId;

      if (applicant) {
        await axios.put(`/api/applicants/${applicant.id}`, payload);
        onClose();
      } else {
        const res = await axios.post('/api/applicants', payload);
        // Link to selected job if any
        const applicantId = res.data?.id;
        if (selectedJobId && applicantId) {
          try {
            await axios.post(`/api/jobs/${selectedJobId}/applicants`, { applicantId });
          } catch (linkErr) {
            console.error('Error linking applicant to job:', linkErr);
          }
        }
        const msg = res.data?.merged
          ? 'Candidate updated (merged with existing record)'
          : 'Candidate added successfully';
        setSuccess(msg);
        setTimeout(() => onClose(), 1500);
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Error saving applicant');
    } finally {
      setLoading(false);
    }
  };

  const isHired = formData.status === 'hired';

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            {applicant ? 'Edit Applicant' : 'Add Applicant'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-red-50 p-4">
            <div className="text-sm text-red-800">{error}</div>
          </div>
        )}

        {success && (
          <div className="mb-4 rounded-md bg-green-50 p-4">
            <div className="text-sm text-green-800">{success}</div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {!applicant && jobs.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Job Posting <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedJobId}
                onChange={handleJobSelect}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">— Select a job —</option>
                {jobs.map((job) => (
                  <option key={job.id} value={job.id}>
                    {job.position} — {job.title}{job.city ? ` (${job.city})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <select
                  name="countryCode"
                  value={formData.countryCode}
                  onChange={handleChange}
                  className="w-24 px-2 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="+91">+91</option>
                  <option value="+1">+1</option>
                  <option value="+44">+44</option>
                  <option value="+971">+971</option>
                  <option value="+65">+65</option>
                  <option value="+61">+61</option>
                </select>
                <input
                  type="tel"
                  name="phone"
                  required
                  placeholder="10 digits"
                  value={formData.phone}
                  onChange={handleChange}
                  onPaste={handlePhonePaste}
                  aria-invalid={phoneHasTooManyDigits}
                  className={`flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                    phoneHasTooManyDigits ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                />
              </div>
              {phoneHasTooManyDigits && (
                <p className="mt-1 text-sm text-red-600">Phone number must be 10 digits only.</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone 2</label>
            <div className="flex gap-2">
              <select
                name="countryCode2"
                value={formData.countryCode2}
                onChange={handleChange}
                className="w-24 px-2 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value="+91">+91</option>
                <option value="+1">+1</option>
                <option value="+44">+44</option>
                <option value="+971">+971</option>
                <option value="+65">+65</option>
                <option value="+61">+61</option>
              </select>
              <input
                type="tel"
                name="phone2"
                placeholder="10 digits"
                value={formData.phone2}
                onChange={handleChange}
                onPaste={handlePhonePaste}
                aria-invalid={phone2HasTooManyDigits}
                className={`flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                  phone2HasTooManyDigits ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
              />
            </div>
            {phone2HasTooManyDigits && (
              <p className="mt-1 text-sm text-red-600">Phone number must be 10 digits only.</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email {!applicant && <span className="text-red-500">*</span>}
              </label>
              <input
                type="email"
                name="email"
                required={!applicant}
                value={formData.email}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Position</label>
              <select
                name="position"
                value={formData.position}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select a position</option>
                {positionOptions.map((positionOption) => (
                  <option key={positionOption} value={positionOption}>
                    {positionOption}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              {APPLICANT_STATUS_OPTIONS.map((statusOption) => (
                <option key={statusOption.value} value={statusOption.value}>
                  {statusOption.label}
                </option>
              ))}
            </select>
          </div>

          {isHired && (
            <div className="border-t pt-4 space-y-4">
              <h4 className="font-medium text-gray-900">Hired Applicant Details</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hire Date</label>
                  <input
                    type="date"
                    name="hireDate"
                    value={formData.hireDate}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Salary</label>
                  <input
                    type="number"
                    step="0.01"
                    name="salary"
                    value={formData.salary}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                  <input
                    type="text"
                    name="department"
                    value={formData.department}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employee ID</label>
                  <input
                    type="text"
                    name="employeeId"
                    value={formData.employeeId}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              name="notes"
              rows="3"
              value={formData.notes}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : applicant ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
      <StatusNoteModal
        isOpen={Boolean(pendingStatus)}
        status={pendingStatus}
        onClose={() => setPendingStatus('')}
        onSave={handleStatusNoteSave}
      />
    </div>
  );
};

export default ApplicantForm;
